import {
  useReceiver,
  useSamplingProcedMuestreo,
  useTypesUnits,
  useUser,
} from "@services/servicesGenericsTables";
import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useSample } from "../../utils/useSample";
import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import { Grid, Typography } from "@mui/material";
import { pointContinuousTable } from "@app/utils/tables/pointContinuous.tables";
import { typesSamplesOptions } from "../../utils/constants";
import { yesNoTable } from "@app/utils/tables/shared.tables";
import { yearsTable } from "@app/utils/tables/years.tables";
import { weeksTable } from "@app/utils/tables/weeks.tables";
import dayjs from "dayjs";

const Sampling = () => {
  const form = useFormContext();
  const { idMuestra } = useParams();
  const { receiverList } = useReceiver();

  const { get, stateSample } = useSample();

  const { userList } = useUser();
  const { typesUnitsis } = useTypesUnits();
  const { samplingProcedureList } = useSamplingProcedMuestreo();
  const sample = get(Number(idMuestra));
  const codInst = sample?.installation?.codigo;
  const codSampletype = sample?.sample_type?.codigo;

  const filterSamplingProcedureList = useMemo(() => {
    return samplingProcedureList.filter(
      (item) => item.codinst === codInst && item.codmue === codSampletype
    );
  }, [samplingProcedureList, codInst, codSampletype]);

  useEffect(() => {
    if (filterSamplingProcedureList.length > 0) {
      form.setValue("proc_take_sample", filterSamplingProcedureList[0]);
    }
  }, [filterSamplingProcedureList, JSON.stringify(form)]);

  const typeSampler = form.watch("type_sampler");

  // Inicializar client_reference con el valor del sampling current o del sample
  useEffect(() => {
    const clientRef = stateSample.sampling.current?.client_reference || sample?.client_reference;
    if (clientRef && !form.getValues("client_reference")) {
      form.setValue("client_reference", clientRef);
    }
  }, [stateSample.sampling.current, sample?.client_reference]);

  useEffect(() => {
    if (typeSampler === "MASL") {
      form.setValue("external_sampler", "");
      form.setValue("externalTakesSampleEntity", "");

      const analystValue = form.getValues("analyst");
      const matchingUser = userList.find(
        (user) => user.username === analystValue?.username
      );

      if (!form.getValues("masl_sampler") && matchingUser) {
        form.setValue("masl_sampler", matchingUser);
      }
    } else if (typeSampler === "Externo") {
      form.setValue("masl_sampler", "");
      form.setValue("proc_take_sample", undefined);
    }
    form.clearErrors([
      "masl_sampler",
      "proc_take_sample",
      "external_sampler",
      "externalTakesSampleEntity",
    ]);
  }, [typeSampler]);

  useEffect(() => {
    const handleFocusProductTimestamp = () => {
      const value = form.getValues("productTimestamp");
      if (!value) {
        const today = dayjs();
        form.setValue("productTimestamp", today);
      }
    };

    const handleFocusFiltrationTimestamp = () => {
      const value = form.getValues("filtrationTimestamp");
      if (!value) {
        const today = dayjs();
        form.setValue("filtrationTimestamp", today);
      }
    };

    const productInputElement = document.querySelector(
      'input[name="productTimestamp"]'
    );
    const filtrationInputElement = document.querySelector(
      'input[name="filtrationTimestamp"]'
    );

    if (productInputElement) {
      productInputElement.addEventListener(
        "focus",
        handleFocusProductTimestamp
      );
    }

    if (filtrationInputElement) {
      filtrationInputElement.addEventListener(
        "focus",
        handleFocusFiltrationTimestamp
      );
    }

    return () => {
      if (productInputElement) {
        productInputElement.removeEventListener(
          "focus",
          handleFocusProductTimestamp
        );
      }

      if (filtrationInputElement) {
        filtrationInputElement.removeEventListener(
          "focus",
          handleFocusFiltrationTimestamp
        );
      }
    };
  }, [JSON.stringify(form)]);

  const watchConservation = form.watch("conservation");
  const watchFiltration = form.watch("filtration");

  useEffect(() => {
    if (!watchConservation?.id) {
      form.setValue("product", "");
      form.setValue("productTimestamp", "");
    }
    if (!watchConservation?.id && !watchFiltration?.id) {
      form.setValue("conservationFiltrationEntity", "");
    }
  }, [watchConservation]);

  useEffect(() => {
    if (!watchFiltration?.id) {
      form.setValue("filter", "");
      form.setValue("filtrationTimestamp", "");
    }
    if (!watchConservation?.id && !watchFiltration?.id) {
      form.setValue("conservationFiltrationEntity", "");
    }
  }, [watchFiltration]);

  useEffect(() => {
    const typeSampler = form.getValues("type_sampler");

    if (!typeSampler) {
      form.setValue("type_sampler", "MASL");
    }
  }, [form]);

  return (
    <>
      <ResponsiveFormContainer columns={5}>
        <MAutocomplete
          key={JSON.stringify(form.getValues("analyst"))}
          name="analyst"
          options={userList}
          label="Seleccione un analista"
          getOptionLabel={(option) => option.username || ""}
          validation={{ required: "El analista es requerido" }}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("year"))}
          name="year"
          options={yearsTable}
          label="Seleccione un año"
          validation={{ required: "El año es requerido" }}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("week"))}
          name="week"
          options={weeksTable}
          label="Seleccione una semana"
          validation={{ required: "La semana es requerida" }}
        />
        <MDatePicker
          name="date_start_sampling"
          pickerType="datetime"
          label="Fecha y hora Inicio Muestreo"
          validation={{
            required: "La fecha y hora de inicio de muestreo es requerida",
          }}
          maxDate={dayjs(form.watch("date_end_sampling"))}
        />
        <MDatePicker
          name="date_end_sampling"
          pickerType="datetime"
          label="Fecha y hora Fin Muestreo"
          validation={{
            required: "La fecha y hora de fin de muestreo es requerida",
          }}
          minDate={dayjs(form.watch("date_start_sampling"))}
        />
      </ResponsiveFormContainer>
      <Grid item xs={12} my={2}>
        <Typography variant="tagText">{"Datos de la muestra"}</Typography>
      </Grid>
      <ResponsiveFormContainer columns={5}>
        <MTextField
          name="client_reference"
          label="Referencia del cliente"
          placeholder="Referencia del cliente"
          type="text"
          fullWidth
        />
        <MTextField
          name="amount_sample"
          label="Cantidad de la muestra (unidades según unidades de la muestra)"
          placeholder="Cantidad de la muestra"
          helperText="Unidades según unidades de la muestra"
          type="number"
          fullWidth
          validation={{ required: "La cantidad de la muestra es requerida" }}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("unit_types"))}
          name="unit_types"
          options={typesUnitsis}
          label="Unidades de la muestra"
          getOptionLabel={(option) =>
            `${option.codigo} | ${option.descripcion}`
          }
          validation={{ required: "El tipo de unidad es requerido" }}
        />
        <MTextField
          name="s2_kg"
          placeholder="S2 Kg/m2"
          label="S2 Kg/m<sup>2</sup>"
          type="number"
          fullWidth
        />
        <MTextField
          name="ll2_m"
          placeholder="LL2, m3 rec"
          label="LL2, m<sup>3</sup> rec"
          type="number"
          fullWidth
        />
        <MTextField
          name="m3_m2"
          label="m<sup>3</sup>/m<sup>2</sup>"
          placeholder="m3/m2"
          type="number"
          fullWidth
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("conservation"))}
          name="conservation"
          options={yesNoTable}
          label="Conservación"
          getOptionLabel={(option) => option.label || ""}
        />

        <MTextField
          name="product"
          placeholder="Producto"
          label="Producto"
          type="text"
          fullWidth
          disabled={!watchConservation?.id}
        />

        <MDatePicker
          name="productTimestamp"
          pickerType="datetime"
          label="Fecha y hora Producto"
          disabled={!watchConservation?.id}
        />
        <MAutocomplete
          key={JSON.stringify(`filtration ${form.getValues("filtration")}`)}
          name="filtration"
          options={yesNoTable}
          label="Filtración"
          getOptionLabel={(option) => option.label || ""}
        />
        <MTextField
          name="filter"
          placeholder="Filtro"
          label="Filtro"
          type="text"
          fullWidth
          disabled={!watchFiltration?.id}
        />
        <MDatePicker
          name="filtrationTimestamp"
          pickerType="datetime"
          label="Fecha y hora Filtración"
          disabled={!watchFiltration?.id}
        />
        <MTextField
          name="conservationFiltrationEntity"
          placeholder="Entidad conservación y/o filtración"
          label="Entidad conservación y/o filtración "
          type="text"
          fullWidth
          disabled={!watchConservation?.id && !watchFiltration?.id}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("samplingType"))}
          name="samplingType"
          options={pointContinuousTable}
          label="Tipo de muestreo"
          getOptionLabel={(option) => option.label || ""}
        />

        <MAutocomplete
          key={JSON.stringify(form.getValues("recipientPostalCode"))}
          name="recipientPostalCode"
          options={receiverList}
          label="Destinatario"
          getOptionLabel={(option) => `${option.codigo} | ${option.nombre}`}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("type_sampler"))}
          name="type_sampler"
          label="Tipo de muestrador"
          options={typesSamplesOptions}
          getOptionLabel={(option) => option}
          validation={{ required: "El tipo de muestrador es requerido" }}
        />
      </ResponsiveFormContainer>

      {form.watch("type_sampler") === "MASL" ? (
        <ResponsiveFormContainer columns={5} my={2}>
          <MAutocomplete
            key={JSON.stringify(form.getValues("masl_sampler"))}
            name="masl_sampler"
            label="Muestreador MASL"
            options={userList}
            getOptionLabel={(option) => option.username || ""}
            validation={{
              required: "El muestrador MASL es requerido",
            }}
          />

          <MAutocomplete
            key={JSON.stringify(form.getValues("proc_take_sample"))}
            name="proc_take_sample"
            label="Proc. Toma muestra"
            options={filterSamplingProcedureList}
            getOptionLabel={(option) => option.proced || ""}
            validation={{
              required:
                filterSamplingProcedureList.length > 0
                  ? "El procedimiento de toma de muestra es requerido"
                  : false,
            }}
          />
        </ResponsiveFormContainer>
      ) : (
        <ResponsiveFormContainer columns={5} my={2}>
          <MTextField
            name="externalTakesSampleEntity"
            placeholder="Entidad conservación y/o filtración"
            label="Entidad externa que toma la muestra"
            type="text"
            fullWidth
          />

          <MTextField
            name="external_sampler"
            label="Muestrador Externo"
            placeholder="Muestrador Externo"
            type="text"
            fullWidth
          />
        </ResponsiveFormContainer>
      )}
    </>
  );
};

export default Sampling;
